import type { Me, Tenant } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../lib/api';
import { useTenantLogo, useLogoOnDark } from '../lib/brand';
import { navigate } from '../lib/router';
import { Button, Dropdown, ErrorText, MenuItem } from '../ui';

/** Le aziende dell'utente; finché una è in preparazione la lista si aggiorna da sola. */
export function useTenants(enabled = true) {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: () => api<{ data: Tenant[] }>('/tenants').then((r) => r.data),
    enabled,
    refetchInterval: (q) => (q.state.data?.some((t) => t.status === 'provisioning') ? 2000 : false),
  });
}

/** Nuova azienda dalla sola partita IVA (ADR 0025): la ragione sociale la trova il sistema; poi la preparazione. */
export function AddCompanyForm({ onDone, compact = false }: { onDone?: () => void; compact?: boolean }) {
  const queryClient = useQueryClient();
  const [vat, setVat] = useState('');
  const create = useMutation({
    mutationFn: () => api<Tenant>('/tenants', { body: { vat_number: vat.replace(/[\s.-]/g, '').toUpperCase() } }),
    onSuccess: (t) => {
      setVat('');
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      onDone?.();
      navigate(`/t/${t.id}/preparazione`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-2">
      <div className="flex gap-2">
        <input
          className="min-w-0 flex-1 rounded-[10px] border border-black/[0.08] bg-paper px-3 py-2 text-sm outline-none focus:border-accent/50 focus:bg-white focus:ring-2 focus:ring-accent/15"
          placeholder="Partita IVA, es. 01234567897"
          value={vat}
          onChange={(e) => setVat(e.target.value)}
          required
          autoComplete="off"
          autoFocus={compact}
          aria-label="Partita IVA della nuova azienda"
        />
        <Button type="submit" disabled={create.isPending || !vat.trim()}>
          {create.isPending ? 'Cerco…' : 'Aggiungi'}
        </Button>
      </div>
      {!compact && <p className="text-xs text-ink/50">La ragione sociale la trova il sistema. Ogni azienda ha un proprio database separato: nessun dato è condiviso con le altre.</p>}
      <ErrorText>{create.error ? errorMessage(create.error) : ''}</ErrorText>
    </form>
  );
}

/** Il logo dell'azienda (ADR 0117) in un riquadro; senza logo, l'iniziale del nome. */
export function TenantLogo({ tenant, size = 'sm' }: { tenant: Tenant; size?: 'sm' | 'md' }) {
  const logo = useTenantLogo(tenant.id, tenant.status === 'active');
  const dark = useLogoOnDark(logo);
  // Riquadro largo: i loghi sono spesso un nome scritto, in un quadrato diventerebbero illeggibili.
  const box = size === 'md' ? 'h-12 w-28 rounded-xl' : 'h-8 w-16 rounded-lg';
  return logo ? (
    <span className={`grid shrink-0 place-items-center overflow-hidden px-1.5 py-1 ring-1 ring-black/[0.06] ${dark ? 'bg-ink' : 'bg-white'} ${box}`}>
      <img src={logo} alt="" className="max-h-full max-w-full object-contain" />
    </span>
  ) : (
    <span className={`grid shrink-0 place-items-center bg-accent/10 font-bold text-accent ${box} ${size === 'md' ? 'text-base' : 'text-xs'}`}>{tenant.name.charAt(0).toUpperCase()}</span>
  );
}

const STATUS: Record<string, string> = { provisioning: 'in preparazione', failed: 'errore', suspended: 'sospesa' };

/** Il nome dell'azienda nella barra: apre l'elenco per passare a un'altra o aggiungerne una con la partita IVA. */
function CompanySwitcher({ current, logo }: { current: Tenant | null; logo?: ReactNode }) {
  const tenants = useTenants();
  const [adding, setAdding] = useState(false);
  return (
    <Dropdown
      label="Cambia azienda"
      trigger={
        <>
          {logo ?? (current && <TenantLogo tenant={current} />)}
          <span className="min-w-0 truncate font-semibold">{current?.name ?? 'Le tue aziende'}</span>
        </>
      }
    >
      {(close) => (
        <>
          <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-ink/45">Le tue aziende</p>
          <div className="max-h-72 overflow-y-auto">
            {(tenants.data ?? []).map((t) => (
              <MenuItem
                key={t.id}
                active={t.id === current?.id}
                onClick={() => {
                  close();
                  setAdding(false);
                  if (t.id !== current?.id) navigate(t.status === 'provisioning' ? `/t/${t.id}/preparazione` : `/t/${t.id}`);
                }}
              >
                <TenantLogo tenant={t} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{t.name}</span>
                  {(t.vat_number || STATUS[t.status]) && (
                    <span className="block truncate text-[11px] font-normal text-ink/45">{[t.vat_number && `P. IVA ${t.vat_number}`, STATUS[t.status]].filter(Boolean).join(' · ')}</span>
                  )}
                </span>
              </MenuItem>
            ))}
          </div>
          <div className="mt-1 border-t border-black/5 pt-1">
            {adding ? (
              <div className="px-2 py-2">
                <AddCompanyForm compact onDone={close} />
              </div>
            ) : (
              <MenuItem onClick={() => setAdding(true)}>
                <span className="grid h-8 w-16 shrink-0 place-items-center rounded-lg border border-dashed border-accent/40 text-accent">+</span>
                Aggiungi un’azienda
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                close();
                navigate(current ? `/t/${current.id}/impostazioni/aziende` : '/impostazioni/aziende');
              }}
            >
              <span className="w-16 shrink-0 text-center text-ink/40">⚙︎</span>
              Gestisci le aziende
            </MenuItem>
          </div>
        </>
      )}
    </Dropdown>
  );
}

const ROLE: Record<Tenant['role'], string> = { owner: 'Titolare', admin: 'Amministratore', member: 'Collaboratore' };

/** Il nome dell'utente nella barra: impostazioni dell'account, assistenti AI, aziende e uscita. */
function UserMenu({ me, current, onLogout }: { me: Me; current: Tenant | null; onLogout: () => void }) {
  const base = current ? `/t/${current.id}/impostazioni` : '/impostazioni';
  return (
    <Dropdown
      label="Menu dell’utente"
      align="right"
      trigger={
        <>
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent/10 text-sm font-semibold text-accent">{me.name.charAt(0).toUpperCase()}</span>
          <span className="hidden text-right leading-tight sm:block">
            <span className="block max-w-[12rem] truncate text-sm font-semibold">{me.name}</span>
            {current && <span className="block text-[11px] text-ink/50">{ROLE[current.role]}</span>}
          </span>
        </>
      }
    >
      {(close) => {
        const go = (path: string) => () => {
          close();
          navigate(path);
        };
        return (
          <>
            <div className="px-3 pb-2 pt-2">
              <p className="truncate text-sm font-semibold">{me.name}</p>
              <p className="truncate text-xs text-ink/50">{me.email}</p>
            </div>
            <div className="border-t border-black/5 pt-1">
              <MenuItem onClick={go(`${base}/account`)}>Account e sicurezza</MenuItem>
              <MenuItem onClick={go(`${base}/assistenti`)}>Collega la tua AI</MenuItem>
              <MenuItem onClick={go(`${base}/aziende`)}>Le tue aziende</MenuItem>
            </div>
            <div className="mt-1 border-t border-black/5 pt-1">
              <MenuItem tone="danger" onClick={onLogout}>
                Esci
              </MenuItem>
            </div>
          </>
        );
      }}
    </Dropdown>
  );
}

/**
 * Barra in alto (ADR 0118): a sinistra l'azienda, che si cambia da qui; a destra l'utente con le impostazioni. `start`
 * = ciò che sta prima del nome (il pulsante del menu sul telefono, il logo), `extra` = azioni della pagina.
 */
export function TopBar({ me, current, onLogout, start, logo, extra }: { me: Me; current: Tenant | null; onLogout: () => void; start?: ReactNode; logo?: ReactNode; extra?: ReactNode }) {
  // La barra resta staccata dal bordo: la fascia sopra (`before:`) ha il colore della pagina, così ciò che scorre sotto
  // non spunta tra il bordo dello schermo e la barra.
  return (
    <header className="sticky top-3 z-30 mb-5 flex h-14 items-center gap-2 rounded-xl bg-white/90 px-2 shadow-[0_4px_24px_rgba(39,32,120,0.06)] backdrop-blur before:pointer-events-none before:absolute before:-inset-x-3 before:-top-3 before:h-3 before:bg-paper lg:top-4 lg:px-3 lg:before:-inset-x-6 lg:before:-top-4 lg:before:h-4">
      {start}
      <div className="min-w-0 flex-1">
        <CompanySwitcher current={current} logo={logo} />
      </div>
      {extra}
      <UserMenu me={me} current={current} onLogout={onLogout} />
    </header>
  );
}
